import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useInventoryStore, InventoryItem } from '../store/inventoryStore';
import { useApiCall } from '../hooks/useApiCall';
import { api } from '../api/api';
import Input from './Input';
import Button from './Button';
import ProviderSelector from './ProviderSelector';
import CustomDropdown from './CustomDropdown';
import Loader from './Loader';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 18px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #FFFFFF;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #609F94;
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1 / -1;
  margin-top: 4px;
`;

const CompactInput = styled(Input)`
  input {
    padding: 6px 10px !important;
    font-size: 14px !important;
  }
  
  label {
    margin-bottom: 2px !important;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #24BA7D;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const ErrorMessage = styled.div`
  color: #FF6B6B;
  font-size: 14px;
  margin-bottom: 8px;
`;

interface Provider {
  name: string;
}

import type { IMetroStation } from "../api/api";

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: InventoryItem | null;
  isLoading?: boolean;
}

const EditProductModal: React.FC<EditProductModalProps> = ({ isOpen, onClose, product, isLoading = false }) => {
  const { updateItem, isUpdating } = useInventoryStore();
  const { executeApiCall } = useApiCall();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [metroStations, setMetroStations] = useState<string[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    color: '',
    size: '',
    price: '',
    cost: '',
    article: '',
    condition: '',
    provider: '',
    city: '',
    place: '',
    fitting: false,
    purchaseDate: '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        model: product.model || '',
        color: product.color || '',
        size: product.size.replace(' US', '') || '',
        price: product.price.replace(/[^\d]/g, '') || '',
        cost: product.cost.replace(/[^\d]/g, '') || '',
        article: product.article || '',
        condition: product.condition || '',
        provider: (product as any).provider_name || (product as any).provider || '',
        city: product.city || '',
        place: product.place || '',
        fitting: product.fitting || false,
        purchaseDate: product.purchaseDate ? product.purchaseDate.split('T')[0] : '',
      });
    }
  }, [product]);

  useEffect(() => {
    const loadData = async () => {
      if (!isOpen) return;
      
      setIsLoadingProviders(true);
      try {
        const [providersResponse, citiesResponse, metroResponse] = await Promise.all([
          api.getProviders(),
          api.getAvailableCities(),
          api.getMetroStations(),
        ]);
        
        setProviders(providersResponse || []);
        setCities(citiesResponse.cities || []);
        setMetroStations(metroResponse.stations || []);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        setIsLoadingProviders(false);
      }
    };

    loadData();
  }, [isOpen]);

  // Управление скроллом body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleProviderChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      provider: value,
    }));
  };

  const handleDropdownChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    const result = await executeApiCall(
      () => updateItem(product.id, {
        name: formData.name,
        model: formData.model,
        size: `${formData.size} US`,
        price: `${formData.price} ₽`,
        cost: `${formData.cost} ₽`,
        article: formData.article,
        condition: formData.condition,
        provider: formData.provider,
        city: formData.city,
        place: formData.place,
        fitting: formData.fitting,
        purchaseDate: formData.purchaseDate,
      }),
      { successMessage: 'Товар успешно обновлен' }
    );

    if (result !== null) {
      onClose();
    }
  };

  if (!isOpen || !product) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Редактировать товар</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {isLoading ? (
          <Loader text="Загрузка актуальных данных товара..." />
        ) : (
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormField>
                <Label>Бренд</Label>
                <CompactInput
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Название бренда"
                  required
                />
              </FormField>

              <FormField>
                <Label>Модель</Label>
                <CompactInput
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="Модель"
                  required
                />
              </FormField>

              <FormField>
                <Label>Цвет</Label>
                <CompactInput
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  placeholder="Цвет"
                  required
                  disabled
                />
              </FormField>

              <FormField>
                <Label>Размер (US)</Label>
                <CompactInput
                  name="size"
                  type="number"
                  step="0.5"
                  value={formData.size}
                  onChange={handleInputChange}
                  placeholder="Размер"
                  required
                />
              </FormField>

              <FormField>
                <Label>Цена продажи (₽)</Label>
                <CompactInput
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="Цена"
                  required
                />
              </FormField>

              <FormField>
                <Label>Цена закупки (₽)</Label>
                <CompactInput
                  name="cost"
                  type="number"
                  value={formData.cost}
                  onChange={handleInputChange}
                  placeholder="Себестоимость"
                  required
                />
              </FormField>

              <FormField>
                <Label>Артикул</Label>
                <CompactInput
                  name="article"
                  value={formData.article}
                  onChange={handleInputChange}
                  placeholder="Артикул"
                  required
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Город"
                  options={cities.map((city) => ({
                    value: city,
                    label: city,
                  }))}
                  value={formData.city}
                  onChange={(value) => handleDropdownChange('city', value)}
                  placeholder="Выберите город"
                />
              </FormField>

              <FormField>
                <CustomDropdown
                  label="Место встречи (станция метро)"
                options={metroStations.map((station) => ({
                  value: station,
                  label: station,
                }))}
                  value={formData.place}
                  onChange={(value) => handleDropdownChange('place', value)}
                  placeholder="Выберите станцию метро"
                />
              </FormField>

              <FormField>
                <Label>Состояние</Label>
                <CompactInput
                  name="condition"
                  value={formData.condition}
                  onChange={handleInputChange}
                  placeholder="Состояние товара"
                />
              </FormField>

              <FormField>
                <Label>Поставщик</Label>
                <ProviderSelector
                  providers={providers}
                  value={formData.provider}
                  onChange={handleProviderChange}
                  placeholder="Выберите или введите поставщика"
                  disabled={isLoadingProviders}
                />
              </FormField>

              <FormField>
                <Label>Дата покупки</Label>
                <CompactInput
                  name="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={handleInputChange}
                  required
                />
              </FormField>

              <CheckboxField>
                <Checkbox
                  type="checkbox"
                  name="fitting"
                  checked={formData.fitting}
                  onChange={handleInputChange}
                />
                <Label>Доступна примерка</Label>
              </CheckboxField>
            </FormGrid>

            <ButtonsContainer>
              <Button type="button" onClick={onClose} variant="secondary">
                Отмена
              </Button>
              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </ButtonsContainer>
          </form>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditProductModal; 